import { useState } from 'react';
import { PackageCard } from '../components/Cards';
import { Counter } from '../components/UI';
import { Icon } from '../components/Icons';
import { PKG_FILTERS } from '../data';
import { usePackages } from '../hooks/usePackages';
import holidaysBanner from '../assets/logo/Find Your Trip at Golden Hour.png';

const formatDate = (dateStr) => {
  if (!dateStr) return 'Not Specified';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

export default function Holidays({ initialFilter = 'All' }) {
  const [travellers, setTravellers] = useState(1);
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [from, setFrom] = useState('');
  const [destination, setDestination] = useState('');
  const [travelDate, setTravelDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const { packages } = usePackages();

  const handleWhatsAppEnquiry = () => {
    const formattedDate = formatDate(travelDate);
    let text = `🌟 *Dira Holidays - Plan Request* 🌟\n`;
    text += `----------------------------------\n`;
    text += `✈️ *Service Type:* Holiday Plan Request\n`;
    text += `📍 *From:* ${from || 'Not Specified'}\n`;
    text += `🗺️ *Destination:* ${destination || 'Not Specified'}\n`;
    text += `📅 *Travel Date:* ${formattedDate}\n`;
    text += `👥 *Travellers:* ${travellers} ${travellers > 1 ? '(Group)' : '(Solo)'}\n`;
    text += `----------------------------------\n`;
    text += `Thank you for choosing Dira Holidays!`;

    const encodedText = encodeURIComponent(text);
    window.open(`https://wa.me/917092210922?text=${encodedText}`, '_blank');
  };

  const filtered = packages.filter(p => {
    // 1. Category / Tag Filter
    const matchesFilter = activeFilter === 'All' ||
      p.cat === activeFilter ||
      p.tag === activeFilter ||
      p.region === activeFilter ||
      (p.tag && p.tag.toLowerCase().includes(activeFilter.toLowerCase())) ||
      (p.cat && p.cat.toLowerCase().includes(activeFilter.toLowerCase())) ||
      (p.region && p.region.toLowerCase().includes(activeFilter.toLowerCase()));

    // 2. Search term Filter (filters by package name only)
    const matchesSearch = !searchTerm ||
      p.name?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  return (
    <div className="page-enter">

      {/* Header with Golden Hour banner background */}
      <div
        style={{
          position: 'relative',
          padding: '210px 24px 44px',
          minHeight: '520px',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          overflow: 'hidden',
          background: '#0d1117',
        }}
      >
        {/* Banner image as background */}
        <img
          src={holidaysBanner}
          alt="Find Your Trip"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 25%',
            zIndex: 0,
          }}
        />

        {/* Soft bottom vignette overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(13,17,23,0.35) 100%)',
            zIndex: 1,
          }}
        />

        {/* Search Card over the banner image */}
        <div style={{ position: 'relative', zIndex: 2, maxWidth: '840px', width: '100%', margin: '0 auto' }}>
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '24px 28px',
              boxShadow: '0 15px 40px rgba(0,0,0,0.22)',
              border: '1px solid rgba(255,255,255,0.8)',
            }}
          >
            <div className="compact-search-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '16px' }}>
              
              <div style={{ textAlign: 'left' }}>
                <label style={{ display: 'block', fontSize: '9px', fontWeight: 800, letterSpacing: '1.5px', color: 'var(--brand)', marginBottom: '5px', textTransform: 'uppercase' }}>From</label>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
                    <Icon name="location" size={14} color="var(--brand)" />
                  </div>
                  <input
                    type="text"
                    placeholder="City"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    style={{ width: '100%', padding: '10px 13px 10px 36px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: '#f8fafc', color: '#0d1117' }}
                  />
                </div>
              </div>

              <div style={{ textAlign: 'left' }}>
                <label style={{ display: 'block', fontSize: '9px', fontWeight: 800, letterSpacing: '1.5px', color: 'var(--brand)', marginBottom: '5px', textTransform: 'uppercase' }}>Destination</label>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
                    <Icon name="globe" size={14} color="var(--brand)" />
                  </div>
                  <input
                    type="text"
                    placeholder="Where to?"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    style={{ width: '100%', padding: '10px 13px 10px 36px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: '#f8fafc', color: '#0d1117' }}
                  />
                </div>
              </div>

              <div style={{ textAlign: 'left' }}>
                <label style={{ display: 'block', fontSize: '9px', fontWeight: 800, letterSpacing: '1.5px', color: 'var(--brand)', marginBottom: '5px', textTransform: 'uppercase' }}>Travel Date</label>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
                    <Icon name="calendar" size={14} color="var(--brand)" />
                  </div>
                  <input
                    type="date"
                    value={travelDate}
                    onChange={(e) => setTravelDate(e.target.value)}
                    style={{ width: '100%', padding: '10px 13px 10px 36px', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: '#f8fafc', color: '#0d1117' }}
                  />
                </div>
              </div>

            </div>
            <button
              onClick={handleWhatsAppEnquiry}
              style={{ width: 'fit-content', margin: '0 auto', padding: '14px 32px', background: 'var(--secondary)', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 800, color: '#0d1117', boxShadow: '0 4px 14px rgba(255,182,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }}
            >
              <Icon name="search" size={16} color="#0d1117" />
              Request to Plan Details
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '36px 32px' }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <h2 style={{ fontSize: '28px', fontWeight: 800, color: '#0d1117', margin: '0 0 6px' }}>All Holiday Packages</h2>
          <p style={{ fontSize: '14px', color: '#6b7a8f', margin: 0 }}>
            Use + / − to switch between solo and group pricing automatically
          </p>
        </div>

        {/* Traveller toggle & Rounded search input */}
        <div style={{ background: '#fff', borderRadius: '12px', padding: '16px 22px', marginBottom: '22px', border: '1.5px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#3d4a5c' }}>Travelling as:</span>
          <Counter value={travellers} onChange={setTravellers} min={1} />
          <span style={{
            fontSize: '10px', fontWeight: 800, padding: '3px 12px', borderRadius: '20px',
            background: travellers > 1 ? 'var(--brand)' : '#f8fafc',
            color: travellers > 1 ? '#fff' : '#6b7a8f',
          }}>
            {travellers > 1 ? `Group of ${travellers} — group pricing` : 'Solo traveller'}
          </span>
          {travellers > 10 && (
            <span style={{ fontSize: '10px', fontWeight: 800, padding: '3px 12px', borderRadius: '20px', background: 'var(--sec-lt)', color: 'var(--sec-dk)' }}>
              ⚡ Large group — MICE team will contact you
            </span>
          )}

          {/* Rounded Search input ("Search Circle") next to Travelling toggle */}
          <div style={{ position: 'relative', marginLeft: 'auto', minWidth: '240px' }}>
            <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
              <Icon name="search" size={14} color="var(--brand)" />
            </div>
            <input
              type="text"
              placeholder="Search packages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 34px',
                border: '1.5px solid #e2e8f0',
                borderRadius: '20px',
                fontSize: '12px',
                color: '#0d1117',
                background: '#f8fafc',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        {/* Filter buttons */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
          {PKG_FILTERS.map(f => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              className={`filter-btn ${activeFilter === f ? 'active' : ''}`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Package grid */}
        {filtered.length > 0 ? (
          <div className="packages-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '18px' }}>
            {filtered.map(pkg => (
              <PackageCard key={pkg.name} pkg={pkg} travellers={travellers} />
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7a8f' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>🗺</div>
            <div style={{ fontSize: '16px', fontWeight: 600 }}>No packages found for this filter</div>
            <div style={{ fontSize: '13px', marginTop: '6px' }}>Try a different category</div>
          </div>
        )}
      </div>
    </div>
  );
}
